/**
 * A SPAN OF SECONDS, THE WAY A GYM SAYS IT.
 *
 * ═══ WHY THIS IS APP INFRASTRUCTURE AND NOT A FEATURE'S OWN HELPER ═══
 *
 * It began as one function inside the logs feature, which was right while a duration was
 * something only a finished session had. It is not any more: the session clock ticks on five
 * screens, the rest between two sets is drawn in the lift log, a heart-rate chart labels its
 * axis, and history prints a metcon time — four features formatting the same quantity.
 *
 * The alternative was one feature importing another's helper, and the direction it would have
 * had to go is the wrong one: the logs feature already imports the sessions feature, so sessions
 * reaching back for this would close a cycle. `src/app/lib/` is the layer both may import and
 * that knows about neither, which is exactly what a shared formatter is.
 *
 * ⚠️ NOT TRANSLATED, AND THAT IS NOT AN OVERSIGHT. `12:04` is digits and a colon; there is no
 * language in it to get wrong, and routing it through `t()` would invite somebody to
 * "translate" the separator. Anything with a WORD in it — "12 min", "1 hr 4 min" — is a
 * sentence and belongs in a locale file, which is why this returns only the bare clock.
 */

/**
 * `M:SS` under an hour, `H:MM:SS` over it.
 *
 * ⚠️ THE MINUTES ARE UNPADDED BELOW AN HOUR AND PADDED ABOVE IT, deliberately. `9:07` is how a
 * nine-minute metcon is said out loud; `09:07` reads as an hour clock with the hours missing.
 * Once there is an hour in front, `1:09:07` needs the pad or the two fields are ambiguous.
 *
 * Negative input clamps to zero: a clock can be wrong about how long something took, but it can
 * never be right about it having taken less than no time, and `-1:-30` on a screen is worse
 * than a zero.
 */
export function clockOf(seconds: number): string {
  const total = Math.max(0, Math.round(seconds));
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const rest = total % 60;
  const pad = (value: number) => String(value).padStart(2, "0");
  return hours > 0 ? `${hours}:${pad(minutes)}:${pad(rest)}` : `${minutes}:${pad(rest)}`;
}
